import React, { useState, useEffect } from 'react';
import {
  Layers,
  Plus,
  Search,
  CheckCircle2,
  Building2,
  Calendar,
  CreditCard,
  Receipt,
  FileCheck,
  Check,
  Clock,
  MessageSquare,
  ShieldCheck,
  DollarSign,
  AlertTriangle,
  FileText,
  BadgeCheck,
} from 'lucide-react';
import {
  ConsolidatedInvoiceItem,
  InvoiceItem,
  UblPaymentAdviceItem,
  UblWithholdingCertItem,
} from '../types/index.ts';
import { WhatsAppShareModal, WhatsAppShareData } from '../components/common/WhatsAppShareModal.tsx';
import { calculateUblPaymentAdvice } from '../services/taxEngine.ts';

interface ConsolidatedInvoicesPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const ConsolidatedInvoicesPage: React.FC<ConsolidatedInvoicesPageProps> = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState<'CONSOLIDATED' | 'PAYMENT_ADVICES' | 'WHT_CERTS'>('CONSOLIDATED');

  const [consolidatedList, setConsolidatedList] = useState<ConsolidatedInvoiceItem[]>([]);
  const [invoices, setInvoices] = useState<InvoiceItem[]>([]);
  const [paymentAdvices, setPaymentAdvices] = useState<UblPaymentAdviceItem[]>([]);
  const [whtCerts, setWhtCerts] = useState<UblWithholdingCertItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCInv, setSelectedCInv] = useState<ConsolidatedInvoiceItem | null>(null);

  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showAdviceModal, setShowAdviceModal] = useState(false);
  const [showCertModal, setShowCertModal] = useState(false);

  // WhatsApp Sharing State
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [shareData, setShareData] = useState<WhatsAppShareData | null>(null);

  // Create form
  const [createForm, setCreateForm] = useState({
    title: 'UBL Karachi Region Branches Batch Renovation Billing',
    dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
    remarks: 'Consolidated batch packet submitted to UBL Head Office Premises Division',
    selectedInvoiceIds: [] as number[],
  });

  // Payment form
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    paymentMethod: 'Bank Transfer (RTGS)',
    accountId: '2', // HBL Bank default
    chequeOrRefNo: 'UBL-RTGS-9901428',
    remarks: 'Central regional disbursement from UBL Head Office',
  });

  // Advice form
  const [adviceForm, setAdviceForm] = useState({
    ublReferenceNo: '',
    adviceDate: new Date().toISOString().split('T')[0],
    relevantPeriod: 'September 2026',
    coveredInvoiceIds: [] as number[],
    otherDeductions: '0',
    bankReference: 'UBL-RTGS-9901428',
    receiptDate: new Date().toISOString().split('T')[0],
    bankAccountId: 2,
    remarks: 'Reconciled with HBL corporate operating account deposit',
  });

  // Cert form
  const [certForm, setCertForm] = useState({
    certificateNumber: '',
    issueDate: new Date().toISOString().split('T')[0],
    taxYear: '2026-2027',
    coveredAdviceIds: [] as number[],
    certifiedWithholdingAmount: '',
    remarks: 'Official UBL annual withholding tax certificate',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [cRes, iRes, aRes, wRes] = await Promise.all([
        fetch('/api/consolidated-invoices'),
        fetch('/api/invoices'),
        fetch('/api/ubl-payment-advices'),
        fetch('/api/ubl-withholding-certificates'),
      ]);

      if (cRes.ok) {
        const cData = await cRes.json();
        if (Array.isArray(cData)) {
          setConsolidatedList(cData);
          if (cData.length > 0 && !selectedCInv) {
            setSelectedCInv(cData[0]);
          }
        }
      }
      if (iRes.ok) {
        const iData = await iRes.json();
        if (Array.isArray(iData)) {
          setInvoices(iData);
        }
      }
      if (aRes.ok) {
        const aData = await aRes.json();
        if (Array.isArray(aData)) {
          setPaymentAdvices(aData);
        }
      }
      if (wRes.ok) {
        const wData = await wRes.json();
        if (Array.isArray(wData)) {
          setWhtCerts(wData);
        }
      }
    } catch (err) {
      console.warn('Consolidated invoices fetch notice:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleToggleInvoiceSelection = (invId: number) => {
    setCreateForm((prev) => {
      const exists = prev.selectedInvoiceIds.includes(invId);
      const updated = exists
        ? prev.selectedInvoiceIds.filter((id) => id !== invId)
        : [...prev.selectedInvoiceIds, invId];
      return { ...prev, selectedInvoiceIds: updated };
    });
  };

  const handleToggleAdviceInvoice = (invId: number) => {
    setAdviceForm((prev) => {
      const exists = prev.coveredInvoiceIds.includes(invId);
      const updated = exists
        ? prev.coveredInvoiceIds.filter((id) => id !== invId)
        : [...prev.coveredInvoiceIds, invId];
      return { ...prev, coveredInvoiceIds: updated };
    });
  };

  const handleCreateConsolidated = async (e: React.FormEvent) => {
    e.preventDefault();
    if (createForm.selectedInvoiceIds.length === 0) {
      alert('Please select at least one branch invoice to consolidate.');
      return;
    }
    try {
      const res = await fetch('/api/consolidated-invoices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: createForm.title,
          dueDate: createForm.dueDate,
          remarks: createForm.remarks,
          invoiceIds: createForm.selectedInvoiceIds,
        }),
      });
      if (res.ok) {
        setShowCreateModal(false);
        setCreateForm({
          title: 'UBL Batch Renovation Billing',
          dueDate: new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
          remarks: '',
          selectedInvoiceIds: [],
        });
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRecordPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCInv) return;
    try {
      const res = await fetch(`/api/consolidated-invoices/${selectedCInv.id}/payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(paymentForm),
      });
      if (res.ok) {
        setShowPaymentModal(false);
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateAdvice = async (e: React.FormEvent) => {
    e.preventDefault();
    if (adviceForm.coveredInvoiceIds.length === 0) {
      alert('Please select at least one invoice covered by the UBL Payment Advice.');
      return;
    }
    try {
      const res = await fetch('/api/ubl-payment-advices', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(adviceForm),
      });
      if (res.ok) {
        setShowAdviceModal(false);
        setAdviceForm({
          ublReferenceNo: '',
          adviceDate: new Date().toISOString().split('T')[0],
          relevantPeriod: 'September 2026',
          coveredInvoiceIds: [],
          otherDeductions: '0',
          bankReference: 'UBL-RTGS-9901428',
          receiptDate: new Date().toISOString().split('T')[0],
          bankAccountId: 2,
          remarks: 'Reconciled with HBL corporate operating account deposit',
        });
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateCert = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await fetch('/api/ubl-withholding-certificates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...certForm,
          coveredAdviceIds: paymentAdvices.map((a) => a.id),
        }),
      });
      if (res.ok) {
        setShowCertModal(false);
        fetchData();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const formatPKR = (val: string | number) => {
    return 'Rs. ' + Math.round(Number(val || 0)).toLocaleString('en-PK');
  };

  // Advice live preview calculation
  const selectedAdviceInvoices = invoices.filter((i) => adviceForm.coveredInvoiceIds.includes(i.id));
  const liveAdviceCalc = calculateUblPaymentAdvice(
    selectedAdviceInvoices.map((i) => ({
      id: i.id,
      invoiceNumber: i.invoiceNumber,
      subtotal: i.subtotal,
      taxAmount: i.taxAmount,
      grandTotal: i.grandTotal,
    })),
    14.0,
    Number(adviceForm.otherDeductions || 0)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              Consolidated Invoicing &amp; UBL Payment Advice
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-blue-100 text-blue-900 border border-blue-200">
              14% Consolidated WHT
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
            Group individual UBL branch claims, process UBL payment advices with 14% withholding on consolidated gross (Subtotal + GST), and reconcile bank deposits.
          </p>
        </div>

        <div className="flex items-center gap-2">
          {activeTab === 'CONSOLIDATED' && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <Plus className="w-4 h-4 text-amber-400" />
              <span>New Consolidated Bill</span>
            </button>
          )}
          {activeTab === 'PAYMENT_ADVICES' && (
            <button
              onClick={() => setShowAdviceModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <DollarSign className="w-4 h-4 text-emerald-200" />
              <span>Process UBL Payment Advice</span>
            </button>
          )}
          {activeTab === 'WHT_CERTS' && (
            <button
              onClick={() => setShowCertModal(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-purple-700 hover:bg-purple-800 text-white rounded-lg text-xs font-semibold shadow-2xs transition-colors"
            >
              <BadgeCheck className="w-4 h-4 text-purple-200" />
              <span>Record WHT Certificate</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 text-xs font-bold">
        <button
          onClick={() => setActiveTab('CONSOLIDATED')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'CONSOLIDATED'
              ? 'border-blue-600 text-blue-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>Consolidated Billing Packets ({consolidatedList.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('PAYMENT_ADVICES')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'PAYMENT_ADVICES'
              ? 'border-emerald-600 text-emerald-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <Receipt className="w-4 h-4" />
          <span>UBL Payment Advices (14% WHT) ({paymentAdvices.length})</span>
        </button>

        <button
          onClick={() => setActiveTab('WHT_CERTS')}
          className={`pb-3 px-3 border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'WHT_CERTS'
              ? 'border-purple-600 text-purple-900'
              : 'border-transparent text-slate-500 hover:text-slate-800'
          }`}
        >
          <BadgeCheck className="w-4 h-4" />
          <span>Withholding Certificates &amp; Reconciliation ({whtCerts.length})</span>
        </button>
      </div>

      {/* TAB 1: CONSOLIDATED BILLING */}
      {activeTab === 'CONSOLIDATED' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Consolidated Packets List */}
          <div className="lg:col-span-1 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 px-1">
              Consolidated Invoices ({consolidatedList.length})
            </h3>
            <div className="space-y-2">
              {consolidatedList.map((cInv) => {
                const isSelected = selectedCInv?.id === cInv.id;
                const outstanding = Number(cInv.totalAmount) - Number(cInv.paidAmount);
                return (
                  <div
                    key={cInv.id}
                    onClick={() => setSelectedCInv(cInv)}
                    className={`p-3.5 rounded-xl border transition-all cursor-pointer text-xs ${
                      isSelected
                        ? 'bg-blue-50/50 border-blue-400 shadow-xs'
                        : 'bg-white border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between font-bold">
                      <span className="text-blue-900">{cInv.consolidatedNumber}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] ${
                          cInv.status === 'PAID'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {cInv.status}
                      </span>
                    </div>
                    <div className="font-semibold text-slate-800 mt-1">{cInv.title}</div>
                    <div className="flex items-center justify-between text-slate-500 mt-2 pt-2 border-t border-slate-100">
                      <span>Total: {formatPKR(cInv.totalAmount)}</span>
                      <span className="font-bold text-slate-900">
                        {outstanding > 0 ? `Due: ${formatPKR(outstanding)}` : 'Fully Settled'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column: Selected Packet Details */}
          <div className="lg:col-span-2">
            {selectedCInv ? (
              <div className="bg-white rounded-xl border border-slate-200 shadow-2xs p-5 space-y-5 text-xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-base font-bold text-slate-900">{selectedCInv.consolidatedNumber}</h2>
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          selectedCInv.status === 'PAID'
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                      >
                        {selectedCInv.status}
                      </span>
                    </div>
                    <p className="text-slate-600 font-semibold mt-0.5">{selectedCInv.title}</p>
                    <p className="text-[11px] text-slate-400 mt-0.5">Due Date: {selectedCInv.dueDate}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setShowPaymentModal(true)}
                      className="px-3.5 py-2 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-2xs flex items-center gap-1.5"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Record Bank Receipt</span>
                    </button>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg text-center">
                  <div>
                    <span className="text-[11px] text-slate-500 block">Total Claim Amount</span>
                    <span className="text-sm font-bold text-slate-900">{formatPKR(selectedCInv.totalAmount)}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500 block">Amount Received</span>
                    <span className="text-sm font-bold text-emerald-700">{formatPKR(selectedCInv.paidAmount)}</span>
                  </div>
                  <div>
                    <span className="text-[11px] text-slate-500 block">Outstanding Balance</span>
                    <span className="text-sm font-bold text-rose-700">
                      {formatPKR(Number(selectedCInv.totalAmount) - Number(selectedCInv.paidAmount))}
                    </span>
                  </div>
                </div>

                {/* Covered Invoices List */}
                <div>
                  <h4 className="font-bold text-slate-800 mb-2">Covered Branch Invoices</h4>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-left text-xs">
                      <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                        <tr>
                          <th className="py-2.5 px-3">Invoice #</th>
                          <th className="py-2.5 px-3">UBL Branch</th>
                          <th className="py-2.5 px-3">Subtotal</th>
                          <th className="py-2.5 px-3">16% GST</th>
                          <th className="py-2.5 px-3 font-bold">Total Claim</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {(selectedCInv.invoices || []).map((inv) => (
                          <tr key={inv.id}>
                            <td className="py-2.5 px-3 font-bold text-blue-900">{inv.invoiceNumber}</td>
                            <td className="py-2.5 px-3">
                              <span className="font-semibold text-slate-800">{inv.branchName}</span>
                              <span className="text-slate-400 block text-[10px]">{inv.branchCode}</span>
                            </td>
                            <td className="py-2.5 px-3 text-slate-600">{formatPKR(inv.subtotal)}</td>
                            <td className="py-2.5 px-3 text-emerald-700 font-medium">{formatPKR(inv.taxAmount)}</td>
                            <td className="py-2.5 px-3 font-bold text-slate-900">{formatPKR(inv.grandTotal)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
                Select a consolidated billing packet to review covered invoices and settlement history.
              </div>
            )}
          </div>
        </div>
      )}

      {/* TAB 2: UBL PAYMENT ADVICES */}
      {activeTab === 'PAYMENT_ADVICES' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-700 flex-shrink-0" />
            <div>
              <strong>UBL 14% Consolidated Withholding Rule:</strong> Income tax withholding is calculated at 14% on the <em>consolidated gross basis</em> (Subtotal + GST). The ERP calculates $(Subtotal + GST) \times 14\%$ and allocates the net disbursement across each covered branch invoice proportionately.
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Advice # / UBL Ref</th>
                    <th className="py-3 px-4">Advice Date / Period</th>
                    <th className="py-3 px-4">Invoices Subtotal</th>
                    <th className="py-3 px-4">16% Sales GST</th>
                    <th className="py-3 px-4 font-bold">Consolidated Basis (Subtotal+GST)</th>
                    <th className="py-3 px-4">14% WHT Deducted</th>
                    <th className="py-3 px-4 font-bold">Net Bank Disbursement</th>
                    <th className="py-3 px-4">Reconciliation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {paymentAdvices.map((adv) => (
                    <tr key={adv.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4">
                        <div className="font-bold text-emerald-950">{adv.adviceNumber}</div>
                        <div className="text-[11px] text-slate-500 font-mono">Ref: {adv.ublReferenceNo}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-700">
                        <div>{adv.adviceDate}</div>
                        <div className="text-[11px] text-slate-500">{adv.relevantPeriod}</div>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{formatPKR(adv.totalInvoicesSubtotal)}</td>
                      <td className="py-3 px-4 text-emerald-700 font-medium">{formatPKR(adv.totalInvoicesGst)}</td>
                      <td className="py-3 px-4 font-bold text-slate-900">{formatPKR(adv.consolidatedBasisAmount)}</td>
                      <td className="py-3 px-4">
                        <span className="font-bold text-amber-900">{formatPKR(adv.consolidatedWithholdingAmount)}</span>
                        <span className="text-[10px] text-slate-500 block">({adv.withholdingRate}%)</span>
                      </td>
                      <td className="py-3 px-4 font-bold text-blue-900 text-sm">
                        {formatPKR(adv.actualAmountReceived || adv.expectedNetPayment)}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="w-3 h-3 text-emerald-700" />
                          {adv.reconciliationStatus}
                        </span>
                        <span className="text-[10px] text-slate-400 block mt-0.5 font-mono">{adv.bankReference}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: WITHHOLDING CERTIFICATES */}
      {activeTab === 'WHT_CERTS' && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-xs text-purple-900 flex items-center gap-3">
            <BadgeCheck className="w-5 h-5 text-purple-700 flex-shrink-0" />
            <div>
              <strong>Audit &amp; Reconciliation:</strong> Reconcile certified withholding amounts on official UBL tax deduction certificates against ERP recorded withholding ledger. Zero-variance certificates are marked MATCHED.
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-2xs overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 uppercase tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Certificate Number</th>
                    <th className="py-3 px-4">Issue Date</th>
                    <th className="py-3 px-4">Tax Year</th>
                    <th className="py-3 px-4">Certified Withholding</th>
                    <th className="py-3 px-4">ERP Recorded Withholding</th>
                    <th className="py-3 px-4">Variance</th>
                    <th className="py-3 px-4">Reconciliation Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {whtCerts.map((cert) => (
                    <tr key={cert.id} className="hover:bg-slate-50/80 transition-colors">
                      <td className="py-3 px-4 font-bold text-purple-950">{cert.certificateNumber}</td>
                      <td className="py-3 px-4 text-slate-600">{cert.issueDate}</td>
                      <td className="py-3 px-4 text-slate-700 font-semibold">{cert.taxYear}</td>
                      <td className="py-3 px-4 font-bold text-purple-900">{formatPKR(cert.certifiedWithholdingAmount)}</td>
                      <td className="py-3 px-4 text-slate-700">{formatPKR(cert.erpRecordedWithholdingAmount)}</td>
                      <td className="py-3 px-4">
                        {Number(cert.varianceAmount) === 0 ? (
                          <span className="text-emerald-700 font-bold">Rs. 0.00 (Zero Variance)</span>
                        ) : (
                          <span className="text-rose-700 font-bold">{formatPKR(cert.varianceAmount)}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            cert.reconciliationStatus === 'MATCHED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-rose-100 text-rose-800'
                          }`}
                        >
                          {cert.reconciliationStatus}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MODAL 1: NEW CONSOLIDATED BILL */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-xl w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Create Consolidated Invoice Packet</h3>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateConsolidated} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Batch Claim Title</label>
                <input
                  type="text"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-semibold"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Due Date</label>
                <input
                  type="date"
                  value={createForm.dueDate}
                  onChange={(e) => setCreateForm({ ...createForm, dueDate: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                  required
                />
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Select Branch Invoices to Group:</label>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {invoices.map((inv) => {
                    const isChecked = createForm.selectedInvoiceIds.includes(inv.id);
                    return (
                      <div
                        key={inv.id}
                        onClick={() => handleToggleInvoiceSelection(inv.id)}
                        className={`p-2.5 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${
                          isChecked ? 'bg-blue-50/60' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="rounded text-blue-600 w-4 h-4"
                          />
                          <div>
                            <span className="font-bold text-blue-900">{inv.invoiceNumber}</span>
                            <span className="text-slate-600 ml-2 font-medium">{inv.branchName}</span>
                          </div>
                        </div>
                        <span className="font-bold text-slate-900">{formatPKR(inv.grandTotal)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-[#0f2942] hover:bg-[#183d5f] text-white rounded-lg font-semibold shadow-2xs"
                >
                  Create Batch Bill
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: PROCESS UBL PAYMENT ADVICE */}
      {showAdviceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-xl w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <div>
                <h3 className="font-bold text-slate-900 text-base">Process UBL Payment Advice</h3>
                <p className="text-xs text-slate-500">Consolidated 14% Withholding on (Subtotal + GST)</p>
              </div>
              <button
                onClick={() => setShowAdviceModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleCreateAdvice} className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">UBL Advice Ref #</label>
                  <input
                    type="text"
                    placeholder="e.g. UBL-HO-ADV-9902"
                    value={adviceForm.ublReferenceNo}
                    onChange={(e) => setAdviceForm({ ...adviceForm, ublReferenceNo: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Advice Date</label>
                  <input
                    type="date"
                    value={adviceForm.adviceDate}
                    onChange={(e) => setAdviceForm({ ...adviceForm, adviceDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Covered Invoices in this Payment:</label>
                <div className="max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {invoices.map((inv) => {
                    const isChecked = adviceForm.coveredInvoiceIds.includes(inv.id);
                    return (
                      <div
                        key={inv.id}
                        onClick={() => handleToggleAdviceInvoice(inv.id)}
                        className={`p-2 flex items-center justify-between cursor-pointer hover:bg-slate-50 ${
                          isChecked ? 'bg-emerald-50/60' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {}}
                            className="rounded text-emerald-600 w-4 h-4"
                          />
                          <span className="font-bold text-slate-900">{inv.invoiceNumber}</span>
                          <span className="text-slate-600 text-[11px]">{inv.branchName}</span>
                        </div>
                        <span className="font-bold text-slate-900">{formatPKR(inv.grandTotal)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* LIVE CONSOLIDATED TAX ENGINE BREAKDOWN */}
              <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-3 space-y-1 text-xs">
                <div className="font-bold text-emerald-950 flex items-center justify-between">
                  <span>Engine Calculated Advice Breakdown:</span>
                  <span className="text-emerald-800 font-semibold">14% Consolidated WHT</span>
                </div>
                <div className="grid grid-cols-2 gap-2 text-slate-700 pt-1">
                  <div>Invoices Subtotal: {formatPKR(liveAdviceCalc.totalSubtotal)}</div>
                  <div>16% Sales GST: {formatPKR(liveAdviceCalc.totalGst)}</div>
                  <div className="font-bold text-slate-900">
                    Consolidated Basis: {formatPKR(liveAdviceCalc.consolidatedBasis)}
                  </div>
                  <div className="font-bold text-amber-900">
                    14% Withholding: {formatPKR(liveAdviceCalc.consolidatedWithholding)}
                  </div>
                </div>
                <div className="pt-2 border-t border-emerald-200 flex items-center justify-between font-bold text-emerald-950 text-sm">
                  <span>Expected Net Bank Deposit:</span>
                  <span>{formatPKR(liveAdviceCalc.expectedNetPayment)}</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Bank Transfer Ref (RTGS)</label>
                  <input
                    type="text"
                    value={adviceForm.bankReference}
                    onChange={(e) => setAdviceForm({ ...adviceForm, bankReference: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Deposit Date</label>
                  <input
                    type="date"
                    value={adviceForm.receiptDate}
                    onChange={(e) => setAdviceForm({ ...adviceForm, receiptDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowAdviceModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Confirm Advice &amp; Settle Invoices
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: WHT CERTIFICATE */}
      {showCertModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-lg w-full p-5 space-y-4 animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Record UBL Withholding Certificate</h3>
              <button
                onClick={() => setShowCertModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleCreateCert} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Certificate #</label>
                <input
                  type="text"
                  placeholder="e.g. WHT-CERT-2026-UBL-02"
                  value={certForm.certificateNumber}
                  onChange={(e) => setCertForm({ ...certForm, certificateNumber: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Issue Date</label>
                  <input
                    type="date"
                    value={certForm.issueDate}
                    onChange={(e) => setCertForm({ ...certForm, issueDate: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block font-semibold text-slate-700 mb-1">Tax Year</label>
                  <input
                    type="text"
                    value={certForm.taxYear}
                    onChange={(e) => setCertForm({ ...certForm, taxYear: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold text-slate-700 mb-1">Certified Withholding Amount (PKR)</label>
                <input
                  type="number"
                  step="0.01"
                  placeholder="e.g. 577741.59"
                  value={certForm.certifiedWithholdingAmount}
                  onChange={(e) => setCertForm({ ...certForm, certifiedWithholdingAmount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                  required
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowCertModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-purple-700 hover:bg-purple-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Save &amp; Reconcile
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 4: RECORD GENERAL PAYMENT */}
      {showPaymentModal && selectedCInv && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-200 max-w-md w-full p-5 space-y-4 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="font-bold text-slate-900 text-base">Record Bank Receipt</h3>
              <button
                onClick={() => setShowPaymentModal(false)}
                className="text-slate-400 hover:text-slate-600 text-sm font-semibold"
              >
                ✕
              </button>
            </div>
            <form onSubmit={handleRecordPayment} className="space-y-3 text-xs">
              <div>
                <label className="block font-semibold text-slate-700 mb-1">Settlement Amount (PKR)</label>
                <input
                  type="number"
                  step="0.01"
                  value={paymentForm.amount || Number(selectedCInv.totalAmount) - Number(selectedCInv.paidAmount)}
                  onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-bold"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowPaymentModal(false)}
                  className="px-3 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg font-semibold"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white rounded-lg font-semibold shadow-2xs"
                >
                  Record Receipt
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* WhatsApp Modal */}
      {shareData && (
        <WhatsAppShareModal
          isOpen={whatsAppModalOpen}
          onClose={() => setWhatsAppModalOpen(false)}
          data={shareData}
        />
      )}
    </div>
  );
};
